import { IUsageEvent } from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { handleApiErrorToast } from '../../../api/axios';
import {
  accountServiceQueryKeys,
  subscriptionServiceQueryKeys,
} from '../../../api/queryKeysService';
import { useVoidUsageEvent } from '../../../api/usageService';
import {
  MAlert,
  MButton,
  MCenterModal,
  MListItem,
  MStack,
  MUnorderedList,
} from '../../../components/Monetize';
import { useToast } from '../../../services/toast';
import { pluralize } from '../../../utils';

export const VoidUsageModal = ({
  usageEvents,
  onClose,
}: {
  usageEvents: IUsageEvent[];
  onClose: () => void;
}) => {
  const isBulk = usageEvents.length > 1;
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: doVoid, isPending: isLoading } = useVoidUsageEvent();

  const handleVoid = async () => {
    const BATCH_SIZE = 5;
    const results: PromiseSettledResult<void>[] = [];
    for (let i = 0; i < usageEvents.length; i += BATCH_SIZE) {
      const batch = usageEvents.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.allSettled(
        batch.map((event) => doVoid({ id: event.id })),
      );
      results.push(...batchResults);
    }
    const failures = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );
    const failureCount = failures.length;
    const successCount = results.length - failureCount;

    if (successCount > 0) {
      queryClient.invalidateQueries({
        queryKey: [...accountServiceQueryKeys.usage.usageListBase()],
      });
      queryClient.invalidateQueries({
        queryKey: [
          ...subscriptionServiceQueryKeys.subscriptionUsageConsumptionBase(),
        ],
      });
    }

    if (failureCount === 0) {
      addToast({
        summary: 'Success',
        detail: `${successCount} usage ${pluralize('record', successCount)} voided successfully.`,
        severity: 'success',
      });
      onClose();
    } else if (successCount > 0) {
      addToast({
        summary: 'Partial Success',
        detail: `${successCount} voided, ${failureCount} failed.`,
        severity: 'warning',
      });
      // Surface at least one failure reason so the user knows why records failed.
      handleApiErrorToast(failures[0].reason);
      onClose();
    } else {
      handleApiErrorToast(failures[0].reason);
    }
  };

  return (
    <MCenterModal
      size="lg"
      isOpen
      modalTitle={
        isBulk
          ? `Void ${usageEvents.length} Usage ${pluralize('Record', usageEvents.length)}`
          : 'Void Usage Record'
      }
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton
            onClick={onClose}
            variant="cancel"
            minW="auto"
            isDisabled={isLoading}
          >
            Cancel
          </MButton>
          <MButton
            onClick={handleVoid}
            isDisabled={isLoading}
            isLoading={isLoading}
            type="submit"
            minW="auto"
          >
            Confirm
          </MButton>
        </MStack>
      )}
    >
      <MAlert type="info" mb={4} title="Before you void">
        <MUnorderedList gap={1} mt={1}>
          <MListItem>Voiding sets the recorded quantity to zero.</MListItem>
          <MListItem>
            A voided record can be edited later to restore a non-zero quantity.
          </MListItem>
          <MListItem>
            Voiding usage from an already-billed period does not update
            previously issued invoices.
          </MListItem>
        </MUnorderedList>
      </MAlert>
    </MCenterModal>
  );
};
